/**
 * Client-side catalog of Tempo packaged skills (mirror of
 * td-agent-gateway `src/skills/skills-catalog.json`). Keep ids in sync.
 * `vertical_demo` is plumbing-only and omitted from the Settings UI.
 */

export interface CopilotSkillCatalogEntry {
  id: string;
  name: string;
  description: string;
  /** Example question to try when entitled. */
  examplePrompt: string;
  billingRoute: string;
}

export const COPILOT_SKILLS_CATALOG: readonly CopilotSkillCatalogEntry[] = [
  {
    id: 'call_center_adherence',
    name: 'Call Center Adherence',
    description:
      'Shift adherence, late starts, and coaching playbooks for contact-center floors.',
    examplePrompt: 'Call center adherence this week',
    billingRoute: '/billing',
  },
  {
    id: 'payroll_readiness',
    name: 'Payroll Readiness',
    description: 'Catch missing hours and timesheet gaps before payroll cut.',
    examplePrompt: 'Payroll readiness for this period',
    billingRoute: '/billing',
  },
  {
    id: 'agency_billable_utilization',
    name: 'Agency Billable Utilization',
    description: 'Project time mix and under-billed utilization for agencies.',
    examplePrompt: 'Billable utilization this month',
    billingRoute: '/billing',
  },
  {
    id: 'healthcare_shift_compliance',
    name: 'Healthcare Shift Compliance',
    description: 'Schedule vs worked time and shift fairness for healthcare teams.',
    examplePrompt: 'Healthcare shift compliance this week',
    billingRoute: '/billing',
  },
  {
    id: 'software_cost_coach',
    name: 'Software Cost Coach',
    description: 'Spot unused apps and license waste from usage patterns.',
    examplePrompt: 'Which apps look unused for my team?',
    billingRoute: '/billing',
  },
  {
    id: 'manager_coaching',
    name: 'Manager Coaching Pack',
    description: 'Supportive 1:1 talk tracks from idle, UAR, and activity signals.',
    examplePrompt: 'Coach me for my 1:1 based on idle spikes',
    billingRoute: '/billing',
  },
  {
    id: 'exec_scorecard',
    name: 'Exec Scorecard',
    description: 'Leadership rollup: WoW hours, productivity, and dashboard links.',
    examplePrompt: 'Executive scorecard this week',
    billingRoute: '/billing',
  },
  {
    id: 'uar_compliance_watch',
    name: 'UAR Compliance Watch',
    description: 'Unusual Activity Report triage for risk and compliance owners.',
    examplePrompt: 'Unusual activity report triage',
    billingRoute: '/billing',
  },
  {
    id: 'meeting_load_coach',
    name: 'Meeting Load Coach',
    description: 'Who spends too much time in meetings vs focus time.',
    examplePrompt: 'Meeting load this week for my team',
    billingRoute: '/billing',
  },
  {
    id: 'benchmarks_companion',
    name: 'Benchmarks AI Companion',
    description: 'Guidance paired with peer productivity benchmark comparisons.',
    examplePrompt: 'How do we compare to peers on productivity?',
    billingRoute: '/billing',
  },
  {
    id: 'idle_spike_playbook',
    name: 'Idle Spike Playbook',
    description: 'Detect idle spikes, identify who, and coach next steps.',
    examplePrompt: 'Who had an idle spike this week?',
    billingRoute: '/billing',
  },
  {
    id: 'new_hire_ramp',
    name: 'New-Hire Ramp Pack',
    description: 'First 30 days hours and activity vs team norms.',
    examplePrompt: 'New hire ramp for this person',
    billingRoute: '/billing',
  },
  {
    id: 'remote_office_pack',
    name: 'Remote vs Office Pack',
    description: 'Hybrid work mix insights with policy-friendly coaching.',
    examplePrompt: 'Remote vs office mix this week',
    billingRoute: '/billing',
  },
  {
    id: 'project_burn_watch',
    name: 'Project Burn Watch',
    description: 'Project totals, leaderboards, and under/over trackers.',
    examplePrompt: 'Project burn watch for this month',
    billingRoute: '/billing',
  },
];

export interface CopilotSkillListItem extends CopilotSkillCatalogEntry {
  entitled: boolean;
}

/** Merge catalog with entitled skill ids from Copilot context / GrowthBook. */
export function buildCopilotSkillsList(entitledIds: readonly string[]): CopilotSkillListItem[] {
  const entitled = new Set(entitledIds);
  return COPILOT_SKILLS_CATALOG.map((skill) => ({
    ...skill,
    entitled: entitled.has(skill.id),
  }));
}
